import { TARIFF_UUIDS } from '../constants.js';

/**
 * Contains information about tariff from OST API.
 *
 * Tariff can have multiple names and descriptions
 * f.e. FTTH may include TV, voice and internet.
 *
 * @property {number[]} uuids
 * @property {string[]} names
 * @property {string[]} descriptions
 * @property {number} price
 */
export class Tariff {
  /**
   * @param {number[]} uuids
   * @param {string[]} names
   * @param {string[]} descriptions
   * @param {number} price
   */
  constructor({ uuids = [], names = [], descriptions = [], price }) {
    this.uuids = uuids;
    this.names = names;
    this.descriptions = descriptions;
    this.price = price;
  }

  /**
   * Returns formatted tariff description.
   * Multiple descriptions are formatted in format "name:`\n`description".
   * In case of only one description, no format is applied to it (only description is returned).
   *
   * @returns {string}
   */
  get description() {
    if (this.descriptions.length === 0) {
      return '';
    }

    const pairs = this.descriptions
      .map((description, index) => [this.names[index], description])
      .filter(pair => pair[1] !== '');

    if (pairs.length === 0) {
      return '';
    }

    if (pairs.length > 1) {
      // multiple, name with description
      const sections = pairs.flatMap(pair => [`${pair[0]}:`, pair[1]]);
      return sections.join('\n');
    }
    // single, only description
    const [, description] = pairs[0];
    return description;
  }

  /**
   * Return a single UUID if `this.uuids` contains only one, or `null` otherwise.
   *
   * @return {?number}
   */
  get singleUuid() {
    if (this.uuids.length !== 1) {
      return null;
    }
    return this.uuids[0];
  }

  /**
   * @return {boolean}
   */
  get isPrima() {
    return [TARIFF_UUIDS.PRIMA_DATA, TARIFF_UUIDS.PRIMA_GO, TARIFF_UUIDS.PRIMA_SAVER].some(
      uuid => uuid === this.singleUuid,
    );
  }

  /**
   * @return {boolean}
   */
  get isCurrentPrima() {
    return this.singleUuid === TARIFF_UUIDS.PRIMA_DATA;
  }

  /**
   * @return {boolean}
   */
  get isOldPrima() {
    return this.isPrima && !this.isCurrentPrima;
  }
}
